<script lang="ts">
  /**
   * WordBoxes - Display word boxes with letter slots for Monster Mode
   * Shows picture clue, empty/filled letter boxes, and progress dots
   */

  import { scale } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import type { TargetWord } from '$lib/types';

  // Props
  interface Props {
    targetWord: TargetWord | null;
  }

  let { targetWord = null }: Props = $props();
</script>

{#if targetWord}
  <div class="word-boxes-container">
    <!-- Word Text with Picture Clue -->
    <div class="word-clue-container">
      {#if targetWord.visible}
        <div class="word-label">
          {targetWord.word}
        </div>
      {/if}
      <div class="emoji">{targetWord.emoji}</div>
    </div>

    <!-- Letter Boxes -->
    <div class="boxes">
      {#each targetWord.boxes as box (box.index)}
        <div
          class="box"
          class:filled={box.filled}
          class:empty={!box.filled}
        >
          {#if box.filled && box.displayLetter}
            <span
              class="letter"
              transition:scale={{ duration: 300, easing: cubicOut }}
            >
              {box.displayLetter.toUpperCase()}
            </span>
          {/if}
        </div>
      {/each}
    </div>

    <!-- Progress Dots -->

  </div>
{/if}

<style>
  .word-boxes-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-md);
    padding: var(--space-md);
  }

  /* Word Clue Container */
  .word-clue-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    min-height: 60px;
  }

  /* Word Label (Easy mode) */
  .word-label {
    font-size: 36px;
    font-weight: bold;
    color: var(--color-bright-yellow);
    letter-spacing: 8px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  .emoji {
    font-size: 48px;
    line-height: 1;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }

  /* Letter Boxes */
  .boxes {
    display: flex;
    gap: 8px;
    padding: var(--space-sm);
  }

  .box {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    font-size: 32px;
    font-weight: bold;
    transition: all 0.3s ease;
    position: relative;
  }

  .box.empty {
    border: 3px dashed rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }

  .box.filled {
    border: 3px solid var(--color-bright-yellow);
    background: linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%);
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.5);
    animation: fillPulse 0.3s ease;
  }

  @keyframes fillPulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
  }

  .letter {
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  /* Progress Dots */
  .progress-dots {
    display: flex;
    gap: 6px;
    margin-top: 4px;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
  }

  .dot.filled {
    background: var(--color-bright-yellow);
    box-shadow: 0 0 10px rgba(251, 191, 36, 0.6);
  }

  /* Responsive */
  @media (max-width: 767px) {
    .word-clue-container {
      gap: var(--space-sm);
      min-height: 40px;
    }

    .emoji {
      font-size: 32px;
    }

    .word-label {
      font-size: 24px;
      letter-spacing: 4px;
    }

    .boxes {
      gap: 6px;
    }

    .box {
      width: 40px;
      height: 40px;
      font-size: 24px;
      border-width: 2px;
    }

    .box.empty {
      border-width: 2px;
    }

    .box.filled {
      border-width: 2px;
    }

    .progress-dots {
      gap: 4px;
    }

    .dot {
      width: 6px;
      height: 6px;
    }
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    .word-clue-container {
      gap: var(--space-sm);
      min-height: 50px;
    }

    .emoji {
      font-size: 40px;
    }

    .word-label {
      font-size: 30px;
      letter-spacing: 6px;
    }

    .box {
      width: 45px;
      height: 45px;
      font-size: 28px;
    }
  }
</style>
